import { AxiosError } from 'axios';
import React, { useRef, useState } from 'react';
import { useMutation } from 'react-query';
import styled, { css } from 'styled-components';

import { Spin } from '@/animation';
import { deleteImage, updateImage, uploadImage } from '@/api';
import FlexWrap from '@/components/common/FlexWrap';
import ToastPopUp from '@/components/UploadImage/ToastPopUp';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import { IDeleteImagePayload, IUploadImagePayload } from '@/types/common.type';
import { comma } from '@/utils/format.util';
import { ICON_IMAGE_EMPTY } from '@repo/assets';
import { TextButton } from '@repo/components';
import { ExpiringImage } from '@repo/components';

const TEXT = {
  KOR: {
    noImageText: '세관에서 도장을 받은\n환급전표 사진을 올려주세요.',
    cruiseNoImageText: '환급전표 업로드까지\n기다려주세요.',
    uploadButton: '환급전표 업로드',
    changeButton: '사진 변경',
    deleteButton: '사진 삭제',
    priceItem1: '품목',
    priceItem2: '결제금액',
    priceItem3: '환급액',
  },
  ENG: {
    noImageText: 'Please upload the receipt with customs stamp',
    cruiseNoImageText: 'Please wait for upload refund receipt',
    uploadButton: 'Upload refund receipt',
    changeButton: 'Change image',
    deleteButton: 'Delete image',
    priceItem1: 'Item',
    priceItem2: 'Payment',
    priceItem3: 'Refund',
  },
};

interface IProps {
  refundIndex: number;
  franchiseeName: string;
  category: string;
  price: number;
  refund: number;
  refundS3Path: string | null;
  refetchHome: () => void;
  isBorder?: boolean;
}

function UploadItem({
  refundIndex,
  franchiseeName,
  category,
  price,
  refund,
  refundS3Path,
  refetchHome,
  isBorder = false,
}: IProps) {
  const dispatch = useAppDispatch();
  const { nationality, passportNumber } = useAppSelector(
    (state) => state,
  ) as IGlobalState;
  const isCruise =
    passportNumber && localStorage.getItem(passportNumber) === 'Cruise';
  const LANG = nationality === 'KOR' ? 'KOR' : 'ENG';
  const [image, setImage] = useState(refundS3Path || null);
  const [isToast, setIsToast] = useState(false);
  const priceInfo = [
    [TEXT[LANG].priceItem1, category],
    [TEXT[LANG].priceItem2, comma(price)],
    [TEXT[LANG].priceItem3, comma(refund)],
  ];
  const inputRef = useRef<HTMLInputElement | null>(null);

  const uploadMutation = useMutation<string, AxiosError, IUploadImagePayload>(
    (payload) => uploadImage(payload),
    {
      retry: false,
      onError: () => {
        alert(
          `[error code:0000] 서버요청에 실패하였습니다. 다시 시도해주세요.`,
        );
      },
      onSuccess: (newS3Url) => {
        setOpenToastTimer();
        setImage(newS3Url);
        refetchHome();
      },
    },
  );
  const updateMutation = useMutation<string, AxiosError, IUploadImagePayload>(
    (payload) => updateImage(payload),
    {
      retry: false,
      onError: () => {
        alert(
          `[error code:0000] 서버요청에 실패하였습니다. 다시 시도해주세요.`,
        );
      },
      onSuccess: (newS3Url) => {
        setOpenToastTimer();
        setImage(newS3Url + `?timestamp=${new Date().getTime()}`);
        refetchHome();
      },
    },
  );
  const deleteMutation = useMutation<number, AxiosError, IDeleteImagePayload>(
    (payload) => deleteImage(payload),
    {
      retry: false,
      onError: () => {
        alert(
          `[error code:0000] 서버요청에 실패하였습니다. 다시 시도해주세요.`,
        );
      },
      onSuccess: () => {
        setImage(null);
        refetchHome();
      },
    },
  );
  const onSubmit = () => {
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: {
        type: 'UPLOAD_IMAGE',
        isActive: true,
        data: { inputRef },
      },
    });
  };
  const onDelete = () => {
    deleteMutation.mutate({ refundIndex });
  };
  const onChangeImage: React.ChangeEventHandler<HTMLInputElement> = (
    e: any,
  ) => {
    const formData = new FormData();
    formData.append('uploadImage', e.target.files[0]);
    if (image) {
      updateMutation.mutate({ refundIndex, formData });
    } else {
      uploadMutation.mutate({ refundIndex, formData });
    }
  };
  const onOpenPreview = () => {
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: {
        type: 'PREVIEW_IMAGE',
        isActive: true,
        data: { imgUrl: image },
      },
    });
  };
  const setOpenToastTimer = () => {
    setIsToast(true);
    setTimeout(() => {
      setIsToast(false);
    }, 4000);
  };

  return (
    <Container dir='column' isBorder={isBorder}>
      <Title>{franchiseeName}</Title>
      <ContentContainer justifyContent='space-between'>
        <PriceItem dir='column' justifyContent='center'>
          {priceInfo.map(([key, value], idx) => (
            <FlexWrap key={idx}>
              <BoldText>{key}</BoldText>
              <Text>{value}</Text>
            </FlexWrap>
          ))}
        </PriceItem>
        {updateMutation.isLoading || uploadMutation.isLoading ? (
          <LoadingBox>
            <LoadingSpinner />
          </LoadingBox>
        ) : (
          <>
            {image ? (
              <ProductImage
                src={image}
                onClick={onOpenPreview}
                onExpired={refetchHome}
              />
            ) : isCruise ? (
              <NoImageContainer
                dir='column'
                justifyContent='center'
                alignItems='center'
                active
                style={{ cursor: 'default' }}
              >
                <NoImage src={ICON_IMAGE_EMPTY} />
                <NoImageText>{TEXT[LANG].cruiseNoImageText}</NoImageText>
              </NoImageContainer>
            ) : (
              <NoImageContainer
                dir='column'
                justifyContent='center'
                alignItems='center'
                active
                onClick={onSubmit}
              >
                <NoImage src={ICON_IMAGE_EMPTY} />
                <NoImageText>{TEXT[LANG].noImageText}</NoImageText>
              </NoImageContainer>
            )}
          </>
        )}
      </ContentContainer>
      <HiddenFileInput
        type='file'
        ref={inputRef}
        accept='image/*'
        name='uploadImage'
        onChange={onChangeImage}
      />
      {!isCruise && (
        <TextButton
          title={image ? TEXT[LANG].changeButton : TEXT[LANG].uploadButton}
          bgColor={image ? '#E9F0FE' : '#246CF6'}
          color={image ? '#246CF6' : '#FFFFFF'}
          isActive
          outline
          onClick={onSubmit}
          margin='24px 0 0'
          width='100%'
          fontSize='14px'
          style={{ fontWeight: 600, letterSpacing: '-0.28px' }}
        />
      )}
      {!isCruise && image && (
        <TextButton
          title={TEXT[LANG].deleteButton}
          bgColor='#E5E6E8'
          onClick={onDelete}
          color='#111'
          margin='12px 0 0'
          width='100%'
          fontSize='14px'
          style={{ fontWeight: 600, letterSpacing: '-0.28px' }}
        />
      )}
      {<ToastPopUp visiable={isToast} />}
    </Container>
  );
}
const Container = styled(FlexWrap)<{ isBorder: boolean }>`
  padding: 24px 0;
  border-bottom: 1px solid #cbccce;
  width: 100%;
  align-items: flex-start;
  ${({ isBorder }) =>
    isBorder &&
    css`
      border-radius: var(--Radius-Card, 8px);
      border: 1px solid var(--KTP-Color-Mono-10, #e5e6e8);
      padding: 24px;
    `}
`;
const ContentContainer = styled(FlexWrap)`
  width: 100%;
`;
const HiddenFileInput = styled.input`
  height: 0;
  opacity: 0;
`;
const PriceItem = styled(FlexWrap)`
  flex: 1;
`;
const Title = styled.h3`
  font-size: 20px;
  margin-bottom: 8px;
  font-weight: 700;
`;
const Text = styled.span`
  font-size: 14px;
  font-weight: 400;
  margin-top: 10px;
`;
const BoldText = styled(Text)`
  text-align: left;
  font-weight: 500;
  width: 70px;
`;
const NoImageContainer = styled(FlexWrap)<{ active: boolean }>`
  background-color: ${(props) => (props.active ? '#E5E6E8' : '#ececec')};
  border-radius: 4px;
  width: 137px;
  height: 98px;
  :hover {
    cursor: pointer;
  }
`;
const ProductImage = styled(ExpiringImage)`
  width: 137px;
  height: 98px;
  border-radius: 4px;
  cursor: pointer;
  :hover {
    opacity: 70%;
  }
  transition: all 300ms ease-in;
`;
const NoImage = styled.img`
  width: 24px;
  height: 24px;
  margin-bottom: 8px;
`;
const NoImageText = styled.span`
  font-size: 12px;
  color: #9fa1a7;
  text-align: center;
  line-height: 150%;
  white-space: pre-wrap;
  letter-spacing: -0.24px;
`;
const LoadingBox = styled(FlexWrap)`
  justify-content: center;
  align-items: center;
  border: 1px solid #9fa1a7;
  border-radius: 4px;
  width: 137px;
  height: 98px;
`;
const LoadingSpinner = styled.div`
  animation: ${Spin} 600ms linear infinite;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  border: 5px solid #fff;
  margin: 0 auto;
  border-top: 5px solid #246cf6;
`;
export default UploadItem;
